import { useState } from 'react'
import { Link } from 'react-router-dom'
import WireframeBanner from './WireframeBanner'
import { PageDesignNote } from './designNotes'
import LevelPill from '../../components/badges/LevelPill'
import ClassifierConfigTable from './components/ClassifierConfigTable'
import Button from '../../components/Button'
import { ClassifierCoverageRow } from './types'
import { ORGANISATION, TEAMS, USERS, PROFILES } from './mockData'
import OrgUsersTable from './components/OrgUsersTable'
import TabBar from './components/TabBar'
import DetailRow from './components/DetailRow'
import PageMasthead from '../../components/PageMasthead'
import { WIREFRAME_PATHS } from './routes'

// Aggregated view of Acme Corp's 88 profiles: the union of classifiers those
// profiles have, with coverage. Hate & Harassment is short by the 4 profiles
// recently added to Acme — Social.
const ORG_CLASSIFIER_ROWS: ClassifierCoverageRow[] = [
    { classifierId: 'hate-harassment', liveVersion: 3, appliedProfiles: 84, totalProfiles: 88 },
    { classifierId: 'spam-scams', liveVersion: 6, appliedProfiles: 88, totalProfiles: 88 },
    { classifierId: 'self-harm', liveVersion: 2, appliedProfiles: 88, totalProfiles: 88 }
]

type Tab = 'Details & notes' | 'Classifiers' | 'Teams' | 'Profiles' | 'Users & access'
const TABS: Tab[] = ['Details & notes', 'Classifiers', 'Teams', 'Profiles', 'Users & access']

// Team list — rendered in the Teams tab. Each team links through to its config page
// so the org page is the entry point into team and profile config.
function TeamList() {
    return (
        <div className="divide-y divide-dashed divide-gray-100 px-4 py-1">
            {TEAMS.map((team) => (
                <div
                    key={team.id}
                    className="flex items-center justify-between py-2.5"
                >
                    <Link
                        to={WIREFRAME_PATHS.teamConfig}
                        className="text-sm font-semibold text-arwen-700 hover:underline"
                    >
                        {team.name}
                    </Link>
                    <span className="text-sm text-gray-800">
                        {team.profileCount} profiles · {team.note}
                    </span>
                </div>
            ))}
        </div>
    )
}

// Profile list — rendered in the Profiles tab. Shows every profile with the team
// it belongs to; profile and team names both link through to their config pages.
function ProfileList() {
    return (
        <table className="w-full border-collapse text-sm">
            <thead>
                <tr className="border-b border-gray-100 text-left">
                    <th
                        scope="col"
                        className="px-4 pb-2 pt-1 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                    >
                        Profile
                    </th>
                    <th
                        scope="col"
                        className="px-4 pb-2 pt-1 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                    >
                        Platform
                    </th>
                    <th
                        scope="col"
                        className="px-4 pb-2 pt-1 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                    >
                        Team
                    </th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
                {PROFILES.map((profile) => (
                    <tr key={profile.id}>
                        <td className="px-4 py-2.5">
                            <Link
                                to={WIREFRAME_PATHS.profileConfig}
                                className="font-semibold text-arwen-700 hover:underline"
                            >
                                {profile.name}
                            </Link>
                        </td>
                        <td className="px-4 py-2.5 text-gray-800">{profile.platform}</td>
                        <td className="px-4 py-2.5">
                            <Link
                                to={WIREFRAME_PATHS.teamConfig}
                                className="text-gray-800 hover:underline"
                            >
                                {profile.teamName}
                            </Link>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

// Organisation details card — rendered in the Details & notes tab.
function OrgDetailsCard() {
    return (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-4 py-2.5 text-[13px] font-semibold text-gray-900">
                Organisation details
            </div>
            <div className="divide-y divide-dashed divide-gray-100 px-4 py-1">
                <DetailRow label="Name" labelWidth="w-24">
                    {ORGANISATION.name}
                </DetailRow>
                <DetailRow label="Key contact" labelWidth="w-24">
                    {ORGANISATION.keyContact.name}
                    <br />
                    <span className="text-xs text-gray-800">{ORGANISATION.keyContact.email}</span>
                </DetailRow>
                <DetailRow label="Teams" labelWidth="w-24">
                    {ORGANISATION.teamCount}
                </DetailRow>
                <DetailRow label="Profiles" labelWidth="w-24">
                    {ORGANISATION.profileCount}
                </DetailRow>
                <DetailRow label="Users" labelWidth="w-24">
                    {USERS.length}
                </DetailRow>
            </div>
        </div>
    )
}

// SD notes card — rendered in the Details & notes tab.
function SdNotesCard() {
    return (
        <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
                <span className="text-[13px] font-semibold text-gray-900">
                    SD notes{' '}
                    <span className="ml-1 text-xs font-normal text-gray-800">Markdown</span>
                </span>
            </div>
            <div className="px-4 py-3">
                <pre
                    className="whitespace-pre-wrap rounded-md border border-gray-100 bg-gray-50 p-3 text-[12.5px] leading-relaxed text-gray-800"
                    style={{ fontFamily: 'inherit' }}
                >
                    {ORGANISATION.notesMarkdown}
                </pre>
                <button
                    type="button"
                    className="mt-2.5 rounded border border-gray-200 bg-white px-2.5 py-1 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                >
                    Edit notes
                </button>
            </div>
        </div>
    )
}

export default function OrgConfigPage({ initialTab = 'Details & notes' }: { initialTab?: Tab }) {
    const [activeTab, setActiveTab] = useState<Tab>(initialTab)

    return (
        <div className="flex min-h-0 flex-1 flex-col bg-gray-50">
            <WireframeBanner />

            <PageDesignNote type="new">
                Org-level configuration — settings, classifiers, and users & access in one place.
                Classifiers apply to profiles only; this page aggregates what the organisation's
                profiles have. Adding a classifier here bulk-adds it to every profile beneath.
            </PageDesignNote>

            <PageMasthead
                breadcrumb={
                    <Link to={WIREFRAME_PATHS.orgList} className="text-arwen-700 hover:underline">
                        Organisations
                    </Link>
                }
                title={ORGANISATION.name}
                levelPill={<LevelPill level="org" label="Organisation" />}
                subtitle={`${ORGANISATION.teamCount} teams · ${ORGANISATION.profileCount} profiles · ${USERS.length} users`}
                actions={<Button text="Edit organisation" primary={false} type="button" size="small" />}
            />

            <TabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

            {/* Tab content */}
            <div className="flex-1 overflow-auto px-6 py-5">
                {activeTab === 'Classifiers' && (
                    <div className="rounded-lg border border-gray-200 bg-white">
                        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
                            <span className="text-[13px] font-semibold text-gray-900">
                                Classifiers across this organisation's profiles
                            </span>
                            <button
                                type="button"
                                className="rounded border border-arwen-200 bg-arwen-50 hover:bg-arwen-100 px-2.5 py-1 text-xs font-semibold text-arwen-700"
                            >
                                + Add classifier
                            </button>
                        </div>
                        <div className="p-4">
                            <ClassifierConfigTable level="org" rows={ORG_CLASSIFIER_ROWS} />
                        </div>
                    </div>
                )}

                {activeTab === 'Teams' && (
                    <div className="rounded-lg border border-gray-200 bg-white">
                        <div className="border-b border-gray-100 px-4 py-2.5 text-[13px] font-semibold text-gray-900">
                            Teams{' '}
                            <span className="ml-1 text-xs font-normal text-gray-800">{TEAMS.length}</span>
                        </div>
                        <TeamList />
                    </div>
                )}

                {activeTab === 'Profiles' && (
                    <div className="rounded-lg border border-gray-200 bg-white">
                        <div className="border-b border-gray-100 px-4 py-2.5 text-[13px] font-semibold text-gray-900">
                            Profiles{' '}
                            <span className="ml-1 text-xs font-normal text-gray-800">
                                Showing {PROFILES.length} of {ORGANISATION.profileCount}
                            </span>
                        </div>
                        <ProfileList />
                    </div>
                )}

                {activeTab === 'Users & access' && (
                    <div className="rounded-lg border border-gray-200 bg-white p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-sm font-semibold text-gray-800">
                                Users &amp; access
                            </h2>
                            <button
                                type="button"
                                className="rounded border border-arwen-200 bg-arwen-50 hover:bg-arwen-100 px-2.5 py-1 text-xs font-semibold text-arwen-700"
                            >
                                + Invite user
                            </button>
                        </div>
                        <OrgUsersTable />
                    </div>
                )}

                {activeTab === 'Details & notes' && (
                    <div className="max-w-lg">
                        <OrgDetailsCard />
                        <SdNotesCard />
                    </div>
                )}
            </div>
        </div>
    )
}
